// ============================================================
// TaskFlow OS - AI Integration Points
// 将来のAI機能統合のための構造設計
// ============================================================

import type { ParsedWorkflow, OperationalNode, Workflow } from '../lib/types';

// ---- AI Provider Interface ----

export interface AIProvider {
  name: string;
  endpoint: string;
  apiKey?: string;
  model?: string;
}

// ---- AI Integration Interfaces ----

// 1. ワークフロー生成
export interface WorkflowGenerationInput {
  prompt: string;
  projectContext?: {
    name: string;
    description: string;
    teamSize: number;
  };
  constraints?: {
    maxNodes: number;
    workflowType: string;
    deadline?: string;
  };
}

export interface WorkflowGenerationOutput {
  workflow: ParsedWorkflow;
  confidence: number;
  suggestions: string[];
}

// 2. タスク分析
export interface TaskAnalysisInput {
  nodes: OperationalNode[];
  workflow: Workflow;
  timeRange?: { start: string; end: string };
}

export interface TaskAnalysisOutput {
  completionRate: number;
  averageCompletionTime: number;
  riskNodes: Array<{
    nodeId: string;
    riskLevel: 'high' | 'medium' | 'low';
    reason: string;
  }>;
  predictions: Array<{
    nodeId: string;
    estimatedCompletion: string;
    confidence: number;
  }>;
}

// 3. ボトルネック検出
export interface BottleneckDetectionInput {
  nodes: OperationalNode[];
  edges: Array<{ source: string; target: string }>;
  eventHistory: Array<{ nodeId: string; timestamp: string; event: string }>;
}

export interface BottleneckDetectionOutput {
  bottlenecks: Array<{
    nodeId: string;
    severity: 'critical' | 'warning' | 'info';
    description: string;
    suggestedAction: string;
    impactedNodes: string[];
  }>;
}

// 4. 自動提案
export interface AutoSuggestionInput {
  context: {
    currentWorkflow: Workflow;
    userHistory: Array<{ action: string; timestamp: string }>;
    projectMetrics: Record<string, number>;
  };
}

export interface AutoSuggestionOutput {
  suggestions: Array<{
    type: 'optimization' | 'resource' | 'timeline' | 'process';
    title: string;
    description: string;
    action: Record<string, unknown>;
    confidence: number;
  }>;
}

// 5. 自動化処理
export interface AutomationInput {
  trigger: {
    eventType: string;
    condition: Record<string, unknown>;
  };
  action: {
    type: string;
    parameters: Record<string, unknown>;
  };
}

export interface AutomationOutput {
  executed: boolean;
  result: unknown;
  nextActions?: AutomationInput[];
}

// ---- AI Service Base Class ----

export abstract class AIService {
  protected provider: AIProvider;

  constructor(provider: AIProvider) {
    this.provider = provider;
  }

  abstract generateWorkflow(input: WorkflowGenerationInput): Promise<WorkflowGenerationOutput>;
  abstract analyzeTask(input: TaskAnalysisInput): Promise<TaskAnalysisOutput>;
  abstract detectBottlenecks(input: BottleneckDetectionInput): Promise<BottleneckDetectionOutput>;
  abstract getSuggestions(input: AutoSuggestionInput): Promise<AutoSuggestionOutput>;
  abstract executeAutomation(input: AutomationInput): Promise<AutomationOutput>;
}

// ---- Default (Rule-based) Implementation ----

export class RuleBasedAIService extends AIService {
  constructor() {
    super({ name: 'rule-based', endpoint: '' });
  }

  async generateWorkflow(input: WorkflowGenerationInput): Promise<WorkflowGenerationOutput> {
    // ルールベースのワークフロー生成（将来AIに置き換え可能）
    const parsed = parseWorkflowText(input.prompt);
    return {
      workflow: parsed,
      confidence: 0.6,
      suggestions: ['AI統合後、より高精度なワークフロー生成が可能になります'],
    };
  }

  async analyzeTask(_input: TaskAnalysisInput): Promise<TaskAnalysisOutput> {
    return {
      completionRate: 0,
      averageCompletionTime: 0,
      riskNodes: [],
      predictions: [],
    };
  }

  async detectBottlenecks(_input: BottleneckDetectionInput): Promise<BottleneckDetectionOutput> {
    return { bottlenecks: [] };
  }

  async getSuggestions(_input: AutoSuggestionInput): Promise<AutoSuggestionOutput> {
    return { suggestions: [] };
  }

  async executeAutomation(_input: AutomationInput): Promise<AutomationOutput> {
    return { executed: false, result: null };
  }
}

// ---- Workflow Text Parser (Rule-based) ----

function parseWorkflowText(text: string): ParsedWorkflow {
  const lines = text.split('\n').filter((l) => l.trim());
  const nodes: ParsedWorkflow['nodes'] = [];
  const edges: ParsedWorkflow['edges'] = [];

  let nodeIndex = 0;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith('//')) continue;

    // Arrow syntax: "Task A -> Task B"
    const arrowMatch = trimmed.match(/^(.+?)\s*->\s*(.+)$/);
    if (arrowMatch) {
      const sourceTitle = arrowMatch[1].trim();
      const targetTitle = arrowMatch[2].trim();

      let sourceNode = nodes.find((n) => n.title === sourceTitle);
      if (!sourceNode) {
        sourceNode = {
          id: `node_${nodeIndex++}`,
          title: sourceTitle,
          description: '',
          type: 'operational',
          dependencies: [],
        };
        nodes.push(sourceNode);
      }

      let targetNode = nodes.find((n) => n.title === targetTitle);
      if (!targetNode) {
        targetNode = {
          id: `node_${nodeIndex++}`,
          title: targetTitle,
          description: '',
          type: 'operational',
          dependencies: [sourceNode.id],
        };
        nodes.push(targetNode);
      } else {
        targetNode.dependencies.push(sourceNode.id);
      }

      edges.push({
        source: sourceNode.id,
        target: targetNode.id,
      });
      continue;
    }

    // List syntax: "- Task Name [priority:high] @assignee"
    const listMatch = trimmed.match(/^[-*]\s*(.+)$/);
    if (listMatch) {
      const content = listMatch[1];
      const priorityMatch = content.match(/\[priority:(\w+)\]/);
      const assigneeMatch = content.match(/@(\S+)/);
      const title = content
        .replace(/\[priority:\w+\]/, '')
        .replace(/@\S+/, '')
        .trim();

      const node: ParsedWorkflow['nodes'][0] = {
        id: `node_${nodeIndex++}`,
        title,
        description: '',
        type: 'operational',
        dependencies: [],
      };

      if (priorityMatch) {
        node.priority = priorityMatch[1] as 'critical' | 'high' | 'medium' | 'low';
      }
      if (assigneeMatch) {
        node.assignee = assigneeMatch[1];
      }

      // Sequential dependency
      if (nodes.length > 0) {
        const prevNode = nodes[nodes.length - 1];
        node.dependencies.push(prevNode.id);
        edges.push({ source: prevNode.id, target: node.id });
      }

      nodes.push(node);
      continue;
    }

    // Plain text: just a task name
    if (trimmed) {
      const node: ParsedWorkflow['nodes'][0] = {
        id: `node_${nodeIndex++}`,
        title: trimmed,
        description: '',
        type: 'operational',
        dependencies: [],
      };

      if (nodes.length > 0) {
        const prevNode = nodes[nodes.length - 1];
        node.dependencies.push(prevNode.id);
        edges.push({ source: prevNode.id, target: node.id });
      }

      nodes.push(node);
    }
  }

  return {
    name: '新規ワークフロー',
    type: 'operational',
    description: 'テキストから生成されたワークフロー',
    nodes,
    edges,
  };
}

// ---- Singleton ----

let currentAIService: AIService = new RuleBasedAIService();

export function getAIService(): AIService {
  return currentAIService;
}

export function setAIService(service: AIService): void {
  currentAIService = service;
  console.log(`[AI] Service switched to: ${service.constructor.name}`);
}

export { parseWorkflowText };
